import Bugsnag from '@bugsnag/js';
import { useQuery } from '@vue/apollo-composable';
import { computed, ref, watchEffect } from 'vue';

import { MoneyV2 } from '@/__generated__/storefront/graphql.ts';
import {
  PatternProduct,
  PatternProductsQueryResult,
  PatternProductsQueryVariables,
  ProductExtended,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import { productTypes } from '@/shared/constants/shopify.js';
import {
  getBulkDiscounts,
  getHighestBulkDiscount,
  useBulkDiscounts
} from '@/shared/utils/discounts.ts';
import { contextualError } from '@/shared/utils/error.ts';
import { shopifyIdToGid } from '@/shared/utils/shopify/id-formatter.ts';

import {
  patternProductQuery,
  patternSizesQuery
} from './patternProductsStorefront.queries.ts';

export const usePatternProducts = (
  patternId: string,
  initialPatternProductsList: PatternProduct[]
) => {
  const processedPatternProducts = ref<PatternProduct[]>(
    initialPatternProductsList
  );
  const allHydratedProducts = ref<ProductExtended[]>([]);
  const queriedProductIds = ref<Set<string>>(new Set());

  const { result: patternSizesResult } = useQuery(
    patternSizesQuery,
    { patternId: shopifyIdToGid(patternId, 'Product') },
    { clientId: 'storefront' }
  );

  const patternSizes = computed(
    () => patternSizesResult?.value?.product?.variants?.nodes || []
  );

  const isValidPatternProduct = (pp: Partial<PatternProduct>) =>
    pp.product_id && pp.variant_id;

  const validPatternProducts = computed(() => {
    if (!processedPatternProducts.value) return [];
    return processedPatternProducts.value.filter(isValidPatternProduct);
  });

  const productIds = computed(() => {
    return validPatternProducts.value
      .filter((pp) => pp?.product_id && pp?.quantity > 0)
      .map((pp) => pp.product_id);
  });

  const newProductIds = computed(() => {
    return productIds.value.filter((id) => !queriedProductIds.value.has(id));
  });

  const incompletePatternProducts = computed(() => {
    if (loading.value) return false;
    if (productIds.value.length <= 0) return false;
    if (
      !processedPatternProducts.value ||
      processedPatternProducts.value.length === 0
    ) {
      //all the products are missing
      return productIds.value.length;
    }

    return (
      Array.from(new Set(productIds.value)).length -
      hydratedProductList.value.length
    );
  });

  const updateQuantity = (index: number, quantity: number) => {
    processedPatternProducts.value[index] = {
      ...processedPatternProducts.value[index],
      quantity
    };
  };

  const updateVariant = (
    index: number,
    productId: string,
    variantId: string,
    quantity: number
  ) => {
    processedPatternProducts.value = processedPatternProducts.value.map(
      (pp, i) => {
        if (i === index && pp.product_id === productId) {
          return { ...pp, variant_id: variantId, quantity };
        }
        return pp;
      }
    );
  };

  const {
    result: resultPatternProducts,
    loading,
    error: patternProductsError
  } = useQuery<PatternProductsQueryResult, PatternProductsQueryVariables>(
    patternProductQuery,
    () => ({
      ids: Array.from(new Set(newProductIds.value)),
      country: window?.Shopify?.country
    }),
    {
      clientId: 'storefront'
    }
  );

  const patternProductsLoading = computed(() => {
    return loading.value && allHydratedProducts.value.length === 0;
  });

  // Watch for new query results and merge them
  watchEffect(() => {
    const queryResult =
      resultPatternProducts.value?.nodes?.filter((a) => a) ?? [];
    if (queryResult.length > 0) {
      const processedResults = queryResult.map((product) => {
        const parsedMetafields = product.metafields.reduce<
          Record<string, string>
        >((metaFieldsObject, metafield) => {
          if (metafield) {
            metaFieldsObject[metafield.key] = metafield.value;
          }
          return metaFieldsObject;
        }, {});

        // calculate bulk discounts from metafield
        const bulkDiscountsMetafield = parsedMetafields['bulk_discounts'];
        const minPrice = product.priceRange.minVariantPrice;
        const maxPrice = product.compareAtPriceRange.minVariantPrice;
        const isOnSale =
          parseFloat(maxPrice.amount) > parseFloat(minPrice.amount);

        const displayBulkDiscount = useBulkDiscounts(
          isOnSale,
          !!bulkDiscountsMetafield
        );
        const bulkDiscounts = displayBulkDiscount
          ? getBulkDiscounts(
              bulkDiscountsMetafield,
              parseFloat(minPrice.amount)
            )
          : [];
        const highestBulkDiscount = getHighestBulkDiscount(bulkDiscounts);

        return {
          ...product,
          brand: parsedMetafields['brand'] || parsedMetafields['pattern_kind'],
          bulkDiscountPercentage: highestBulkDiscount?.discount_percentage || 0,
          bulkDiscounts
        };
      });

      // Merge new results with existing ones, avoiding duplicates
      const existingIds = new Set(allHydratedProducts.value.map((p) => p.id));
      const newResults: ProductExtended[] = processedResults.filter(
        (p) => !existingIds.has(p.id)
      );
      allHydratedProducts.value = [...allHydratedProducts.value, ...newResults];

      // Mark these product IDs as queried
      newProductIds.value.forEach((id) => {
        queriedProductIds.value.add(id);
      });
    }
  });

  const hydratedProductList = computed<ProductExtended[]>(() => {
    // Return only the products that are currently in the pattern
    const currentProductIds = new Set(productIds.value);
    return allHydratedProducts.value.filter((product) =>
      currentProductIds.has(product.id)
    );
  });

  watchEffect(() => {
    if (!Bugsnag.isStarted()) {
      return;
    }

    if (incompletePatternProducts.value) {
      const incompletePatternProductsError = contextualError(
        'usePatternProducts',
        'Incomplete pattern products, some products are null',
        {
          data: initialPatternProductsList
        }
      );

      if (incompletePatternProductsError) {
        Bugsnag.notify(incompletePatternProductsError);
      }
    }

    if (patternProductsError.value) {
      const patternProductsContextualError = contextualError(
        'usePatternProducts',
        patternProductsError.value,
        {
          data: initialPatternProductsList
        }
      );

      if (patternProductsContextualError) {
        Bugsnag.notify(patternProductsContextualError);
      }
    }
  });

  const getVariantWeight = (variant: Variant, productType: string) => {
    const metafield = variant.metafield?.value;
    if (metafield && productType === productTypes.YARN) {
      const dimensions = JSON.parse(metafield);
      return `${dimensions.weight}g`;
    }

    return '';
  };

  const getDiscountPercentage = (
    price: MoneyV2,
    compareAtPrice: MoneyV2 | null
  ) => {
    const oldPrice = compareAtPrice ? parseFloat(compareAtPrice.amount) : null;

    if (oldPrice && oldPrice > 0) {
      return Math.floor(
        ((oldPrice - parseFloat(price.amount)) * 100) / oldPrice
      );
    }

    return 0;
  };

  const handleSizeChange = (size: string) => {
    const selectedPatternSize = patternSizes.value?.find(
      (ps) => ps.title === size
    );

    const ppMetafield = selectedPatternSize?.metafield?.value;
    if (!ppMetafield) return;

    if (ppMetafield) {
      const newMetafield: PatternProduct[] = JSON.parse(ppMetafield);

      const newPatternProducts: PatternProduct[] = [];
      const deletedPatternProducts: PatternProduct[] = [];
      const matchedProducts = new Set<PatternProduct>();

      // handle new information
      newMetafield.forEach((newPP) => {
        if (!isValidPatternProduct(newPP)) return; // skip invalid entries

        const existingProduct = processedPatternProducts.value.find(
          (currentPP) =>
            currentPP.product_id === newPP.product_id &&
            currentPP.variant_id === newPP.variant_id
        );

        if (existingProduct) {
          newPatternProducts.push({
            ...existingProduct,
            quantity: newPP.quantity ?? 1
          });
          matchedProducts.add(existingProduct);
        } else {
          newPatternProducts.push(newPP);
        }
      });

      const newPatternProductsMap = new Map(
        newPatternProducts.map((pp) => [
          `${pp.product_id}-${pp.variant_id}`,
          pp.quantity
        ])
      );

      // handle deleted products
      processedPatternProducts.value.forEach((currentPP) => {
        const key = `${currentPP.product_id}-${currentPP.variant_id}`;

        const isPresentInNew = newPatternProductsMap.has(key);

        if (!isPresentInNew && !matchedProducts.has(currentPP)) {
          deletedPatternProducts.push({
            ...currentPP,
            quantity: 0 // products with zero quantity will be filtered out, but preserved for future reference
          });
        }
      });

      processedPatternProducts.value = [
        ...newPatternProducts,
        ...deletedPatternProducts
      ];
    }
  };

  const currentPatternProducts = computed(() => {
    return processedPatternProducts.value.filter(
      (pp) => pp.quantity > 0 && isValidPatternProduct(pp)
    );
  });

  return {
    hydratedProductList,
    patternProductsLoading,
    incompletePatternProducts,
    patternSizes,
    getVariantWeight,
    getDiscountPercentage,
    handleSizeChange,
    currentPatternProducts,
    updateQuantity,
    updateVariant
  };
};
